// Simple toast notification utility
type ToastType = 'success' | 'error' | 'info';

interface ToastOptions {
    duration?: number;
    type?: ToastType;
}

class Toast {
    private container: HTMLDivElement | null = null;

    private getContainer(): HTMLDivElement {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.id = 'toast-container';
            this.container.style.cssText = `
                position: fixed;
                top: 1rem;
                right: 1rem;
                z-index: 9999;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                pointer-events: none;
            `;
            document.body.appendChild(this.container);
        }
        return this.container;
    }

    private show(message: string, options: ToastOptions = {}) {
        const { duration = 3000, type = 'info' } = options;
        const container = this.getContainer();

        const toast = document.createElement('div');
        toast.style.cssText = `
            padding: 0.75rem 1rem;
            border-radius: 0.5rem;
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
            font-size: 0.875rem;
            font-weight: 500;
            pointer-events: auto;
            animation: slideIn 0.3s ease-out;
            max-width: 20rem;
            word-wrap: break-word;
        `;

        // Set colors based on type
        if (type === 'success') {
            toast.style.backgroundColor = '#10b981';
            toast.style.color = 'white';
        } else if (type === 'error') {
            toast.style.backgroundColor = '#ef4444';
            toast.style.color = 'white';
        } else {
            toast.style.backgroundColor = '#3b82f6';
            toast.style.color = 'white';
        }

        toast.textContent = message;
        container.appendChild(toast);

        // Add animation keyframes if not already added
        if (!document.getElementById('toast-animations')) {
            const style = document.createElement('style');
            style.id = 'toast-animations';
            style.textContent = `
                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                @keyframes slideOut {
                    from {
                        transform: translateX(0);
                        opacity: 1;
                    }
                    to {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                }
            `;
            document.head.appendChild(style);
        }

        // Auto remove after duration
        setTimeout(() => {
            toast.style.animation = 'slideOut 0.3s ease-in';
            setTimeout(() => {
                container.removeChild(toast);
            }, 300);
        }, duration);
    }

    success(message: string, duration?: number) {
        this.show(message, { type: 'success', duration });
    }

    error(message: string, duration?: number) {
        this.show(message, { type: 'error', duration });
    }

    info(message: string, duration?: number) {
        this.show(message, { type: 'info', duration });
    }
}

export const toast = new Toast();
